// Global packages and functions
import { useCallback, useEffect, useState } from 'react'
import { buildVariantOption, checkNumberOfDays, toFixedNumber } from '~/utils'
import { useDebugLogger, useProductDates } from '~/hooks'

// Types
import type {
  BrendaChargesErrorState,
  BrendaChargesResult,
} from '~/types/brendaCharges'
import type {
  CustomerAddressProps,
  LunarProductPreBasket,
  PriceValue,
} from '~/types'

interface Props {
  fromPrice: PriceValue | null
  salePrice: PriceValue | null
  buyProduct?: boolean
  allowSaturday?: boolean
  fulfilment: string | null
  averageHire?: number | null
  /** The resolved charges result from `useBrendaCharges`, or null. */
  charges?: BrendaChargesResult | null
  /** Latest charges fetch error, passed straight through to the date hook. */
  pricesError: BrendaChargesErrorState | null
  setFulfilment?: (e: string | null) => void
}

type GuardResult<T> = { ok: true; data: T } | { ok: false; message: string }

type ChargesSubmitRequest = {
  stockCode: string
  startDate: string
  endDate: string
  quantity: string
  postcode: string
  address: { line: string; data: CustomerAddressProps }
  selectedFulfilment?: 'delivery' | 'pickup'
  currentBranchId?: number | null
}

// Re-export for consumers that import from useProductData
export type { DateChangeArgs } from '~/hooks/useProductDates'

const MAX_QTY = {
  hire: 10,
  buy: 1000,
} as const

/**
 * useProductData
 *
 * The PDP add-to-basket control hook. Owns:
 *
 *   1. Local hire/buy UI state — quantity, start/end dates, derived
 *      day count, pricing display.
 *   2. Date validation (delegated to `useProductDates`).
 *   3. A `buildBasketItem` helper that turns a live charges result +
 *      current selections into a `LunarProductPreBasket` line item
 *      ready to hand off to the basket flow.
 *   4. A `buildProductChangeRequest` helper that produces a
 *      `BrendaChargesSubmit` for re-fetching prices on quantity /
 *      fulfilment / date changes.
 *
 * Consumes `BrendaChargesResult` directly. The add-to-basket flow
 * reads charges live off the context at commit time.
 */
const useProductData = ({
  fromPrice,
  salePrice,
  buyProduct = false,
  allowSaturday = false,
  fulfilment,
  averageHire,
  charges,
  pricesError,
  setFulfilment,
}: Props) => {
  // ----------------------------
  // Debug logging
  // ----------------------------
  const debugRef = useDebugLogger('DEBUG_PDP_FLOW')

  // ----------------------------
  // Date state + validation (shared with basket modals)
  // ----------------------------

  const {
    startDate,
    endDate,
    daysDifference,
    dateError: productError,
    minDate,
    setDateError: setProductError,
    handleStartDateChange,
    handleEndDateChange,
    handleBuyDeliveryDateChange,
  } = useProductDates({ buyProduct, allowSaturday, averageHire, pricesError })

  // ----------------------------
  // UI / output state
  // ----------------------------

  const [quantity, setQuantity] = useState<string>('1')
  const [price, setPrice] = useState<PriceValue | null>(fromPrice)
  const [priceSale, setPriceSale] = useState<PriceValue | null>(salePrice)

  // Offer prices for schema meta
  const offerAmount =
    priceSale?.amount ??
    price?.amount ??
    salePrice?.amount ??
    fromPrice?.amount ??
    null
  const offerCurrency =
    priceSale?.currency ??
    price?.currency ??
    salePrice?.currency ??
    fromPrice?.currency ??
    'GBP'

  // Keep the displayed base price in sync with props while no charges
  // result is present — once charges land, the fetched price takes over.
  useEffect(() => {
    if (!charges) setPrice(fromPrice)
  }, [fromPrice, charges])

  useEffect(() => {
    if (!charges) setPriceSale(salePrice)
  }, [salePrice, charges])

  // ----------------------------
  // Small helpers
  // ----------------------------

  const parseQuantity = useCallback((q: string) => {
    const n = Number(q)
    if (!Number.isFinite(n) || n <= 0) return 1
    return Math.floor(n)
  }, [])

  // ----------------------------
  // Charges-driven UI side-effects
  // ----------------------------
  // - Update the displayed price when charges arrive or base prices change.

  useEffect(() => {
    const chargesPrice = charges?.product?.price
    if (chargesPrice?.amount != null) {
      setPrice({
        amount: toFixedNumber(chargesPrice.amount, 2),
        currency: chargesPrice.currency,
      })
      setPriceSale(null)
      debugRef.current('[useProductData] charges price applied:', chargesPrice)
      return
    }

    // Fallback to base prices
    setPrice(fromPrice)
    setPriceSale(salePrice)
  }, [charges, fromPrice, salePrice])

  // ----------------------------
  // Build a basket-ready line item from the live charges result
  // ----------------------------
  //
  // Called at commit time with the latest charges snapshot + the user's
  // selected quantity / title. Produces a `LunarProductPreBasket` shape
  // with pickup/delivery date logic applied and the hire end date
  // bumped past any bank holidays.

  const mapBasketProduct = useCallback(
    (
      result: BrendaChargesResult,
      isBuy: boolean,
      title: string | undefined,
      qty: number,
      option?: { name: string; value: string }
    ): LunarProductPreBasket | null => {
      const { product, delivery, pickup, request } = result

      // Variant descriptor for display under the basket line title
      // ("Size: 9.5M"). Brenda sends a `Default`/`default` placeholder pair
      // on products that have no real variant, so both halves must be
      // present and neither may be that placeholder.
      const variantOption = buildVariantOption(option)

      debugRef.current('[useProductData] mapBasketProduct called:', {
        stockCode: product?.stockCode,
        isBuy,
        fulfilment,
      })

      const priceValue = product?.price?.amount
      if (priceValue == null) {
        debugRef.current(
          '[useProductData] mapBasketProduct returned null (missing price)'
        )
        return null
      }

      // ---- BUY item ----
      if (isBuy) {
        const item: LunarProductPreBasket = {
          productType: 'consumable',
          stockCode: product.stockCode,
          quantity: qty,
          price: priceValue,
          fulfilment: 'delivery',
          deliveryPrice: delivery.price?.amount ?? 0,
          deliveryDate: delivery.date ?? '',
          lineGroupId: product.groupId,
          requiresFraudCheck: Boolean(product.requiresFraudCheck),
          title,
          variantOption,
          maxQuantity: delivery.maxQuantity,
        }

        debugRef.current('[useProductData] mapped buy item:', item)
        return item
      }

      // ---- HIRE item ----
      const effectiveFulfilment =
        (fulfilment as 'delivery' | 'pickup' | null) ??
        request.earliestFulfilment ??
        'delivery'

      const deposit = toFixedNumber(product.deposit?.amount ?? 0, 2)
      const damageWaiver = toFixedNumber(product.damageWaiver ?? 0, 2)

      // `useBrendaCharges` already re-fetches on fulfilment switch and
      // tracks the active side's window in `request.resolvedStart/End`,
      // so these *are* the dates the line should bill on. No per-side
      // dance or end-date recompute needed here.
      const startDateToUse = request.resolvedStartDate
      const endDateToUse = request.resolvedEndDate

      // The user's original requested duration — anchored on the
      // first-asked-for range preserved by `useBrendaCharges`. Written
      // to the basket line as `hire_days_requested` so reporting
      // reflects what the user actually asked for, not any subsequent
      // BH-bumped resolved window.
      const requestedDuration = checkNumberOfDays(
        request.originalStartDate,
        request.originalEndDate
      )

      const item: LunarProductPreBasket = {
        productType: 'bulk',
        stockCode: product.stockCode,
        quantity: qty,
        price: priceValue,
        fulfilment: effectiveFulfilment,
        deliveryPrice: delivery.price?.amount ?? 0,
        deliveryDate: delivery.date ?? '',
        hireDeposit: deposit,
        hireDamageWaiver: damageWaiver,
        hireStartDateRequested: request.originalStartDate,
        lineGroupId: product.groupId,
        requiresFraudCheck: Boolean(product.requiresFraudCheck),
        title,
        variantOption,
        maxQuantity:
          effectiveFulfilment === 'pickup'
            ? pickup.maxQuantity
            : delivery.maxQuantity,
        hireDaysRequested: requestedDuration,
        hireStartDate: startDateToUse,
        hireEndDate: endDateToUse,
      }

      // Pickup info — always include when pickup is viable so the cart
      // knows pickup is an option regardless of the currently selected
      // fulfilment (mirrors how delivery fields are always written above).
      if (pickup.available && pickup.branchId && pickup.branchAddress) {
        item.pickupBranchId = pickup.branchId
        item.pickupBranchAddress = pickup.branchAddress
        item.pickupDate = pickup.date ?? request.resolvedStartDate

        if (pickup.branchDistance) {
          item.pickupBranchDistance = pickup.branchDistance
        }
      }

      debugRef.current('[useProductData] mapped hire item:', item)
      return item
    },
    [fulfilment]
  )

  // ----------------------------
  // Hire-only: default fulfilment bootstrap
  // ----------------------------

  // Buy products are delivery-only — set fulfilment immediately.
  useEffect(() => {
    if (buyProduct && !fulfilment && setFulfilment) {
      setFulfilment('delivery')
    }
  }, [buyProduct, fulfilment, setFulfilment])

  // Hire-only: seed the default fulfilment from the evaluator's earliest
  // pick, and fall back off pickup if it stops being available between
  // re-fetches.
  useEffect(() => {
    if (!charges || buyProduct || !setFulfilment) return

    const pickupAvailable = charges.pickup.available

    // Safety: pickup selected but no longer available → switch to delivery.
    if (fulfilment === 'pickup' && !pickupAvailable) {
      debugRef.current(
        '[useProductData] pickup selected but unavailable; forcing delivery'
      )
      setFulfilment('delivery')
      return
    }

    // If nothing is chosen yet, use the evaluator's earliest pick.
    if (!fulfilment) {
      const defaultFulfilment =
        charges.request.earliestFulfilment ??
        (pickupAvailable ? 'pickup' : 'delivery')

      debugRef.current(
        '[useProductData] default fulfilment:',
        defaultFulfilment
      )
      setFulfilment(defaultFulfilment)
    }
  }, [buyProduct, charges, fulfilment, setFulfilment])

  // ----------------------------
  // Reusable helpers (component-friendly)
  // ----------------------------

  const getAddToCartError = useCallback(
    (args: {
      mode: 'hire' | 'buy'
      charges?: BrendaChargesResult | null
      address?: string | null
    }): string | null => {
      const qty = Number(quantity)

      if (!Number.isFinite(qty)) return 'Quantity is invalid'

      const max = args.mode === 'buy' ? MAX_QTY.buy : MAX_QTY.hire
      if (qty > max) return `You can order a maximum quantity of ${max}`

      // Hire requires dates
      if (args.mode === 'hire' && (!startDate || !endDate)) {
        return 'Missing dates for hire period'
      }

      if (!args.address) return 'Missing customer address details'

      const result = args.charges
      if (!result) {
        return args.mode === 'buy'
          ? 'Cant find relevant price data'
          : 'Cant find relevant hire data'
      }

      if (!result.product.groupId) return 'Missing line group id'
      if (!result.product.price?.amount) return 'Missing product price'

      if (args.mode === 'hire') {
        if (!result.delivery.available && !result.pickup.available) {
          return 'Product not available for your selected dates'
        }
        return null
      }

      // Buy-specific
      if (!result.delivery.available) return 'Product not available'
      return null
    },
    [startDate, endDate, quantity]
  )

  const buildBasketItem = useCallback(
    (args: {
      mode: 'hire' | 'buy'
      charges?: BrendaChargesResult | null
      address?: string | null
      title?: string
      option?: { name: string; value: string }
    }): GuardResult<LunarProductPreBasket> => {
      const err = getAddToCartError(args)
      if (err) return { ok: false, message: err }

      const qty = parseQuantity(quantity)
      const item = mapBasketProduct(
        args.charges!,
        args.mode === 'buy',
        args.title,
        qty,
        args.option
      )

      if (!item) return { ok: false, message: 'Sorry, Something went wrong' }
      return { ok: true, data: item }
    },
    [getAddToCartError, mapBasketProduct, parseQuantity, quantity]
  )

  /**
   * Builds a `BrendaChargesSubmit`-shaped request for the charges hook.
   * Forwards the current `fulfilment` selection so `resolvedStartDate`
   * stays anchored on the user's committed side. Without it, a quantity
   * bump that shifts one side past the other can leave the basket line
   * with `hire_start_date` on a different side from `selected_fulfilment`,
   * driving the canonical/shadow apart on commit.
   */
  const buildProductChangeRequest = useCallback(
    (args: {
      stockCode?: string | null
      address?: string | null
      addressData?: CustomerAddressProps | null
      quantity: string
      /**
       * The branch the user explicitly chose, if any. Forwarded as a
       * non-strict `currentBranchId` on pickup re-fetches so Brenda
       * keeps them on that branch when it still has stock, falling back
       * to the nearest viable one otherwise (mirrors the basket).
       */
      currentBranchId?: number | null
    }): GuardResult<ChargesSubmitRequest> => {
      const sc = args.stockCode
      const ad = args.address
      const adData = args.addressData

      if (!sc || !startDate || !endDate || !ad || !adData) {
        return { ok: false, message: 'Missing info to fetch prices' }
      }

      // Only forward `fulfilment` when it's a known side. The first-time
      // "Get price" submit runs before any selection exists; the hook
      // then picks the earliest side and `useProductData` sets
      // `fulfilment` from `request.earliestFulfilment` on response.
      const selectedFulfilment =
        fulfilment === 'pickup' || fulfilment === 'delivery'
          ? fulfilment
          : undefined

      // Re-pin the chosen branch only when committed to pickup.
      const currentBranchId =
        selectedFulfilment === 'pickup' && args.currentBranchId != null
          ? args.currentBranchId
          : undefined

      return {
        ok: true,
        data: {
          stockCode: sc,
          startDate,
          endDate,
          quantity: args.quantity,
          postcode: adData.postcode,
          address: { line: ad, data: adData },
          ...(selectedFulfilment && { selectedFulfilment }),
          ...(currentBranchId != null && { currentBranchId }),
        },
      }
    },
    [startDate, endDate, fulfilment]
  )

  // ----------------------------
  // Hook API
  // ----------------------------
  return {
    // State
    quantity,
    price,
    priceSale,
    startDate,
    endDate,
    daysDifference,
    productError,
    minDate,
    offerAmount,
    offerCurrency,

    // Setters
    setQuantity,
    setProductError,

    // Helpers
    buildBasketItem,
    buildProductChangeRequest,
    handleStartDateChange,
    handleEndDateChange,
    handleBuyDeliveryDateChange,
  }
}

export default useProductData
